import type { ReactNode } from 'react';

/**
 * Os eventos de uma FASE — o que acontece quando um cartão entra nela.
 *
 * Moram na fase, e não no quadro: a fase é o dado que o quadro e a tabela têm
 * em comum, e mudar a fase de um cartão é o mesmo ato nas duas visões —
 * arrastar a coluna, escolher "Mudar fase" na linha, ou a rota da API. Quem
 * DISPARA o evento é o lugar que muda a fase (na feature de verdade, o
 * serviço do módulo no servidor), nunca uma visão.
 *
 * Nada aqui sabe o que o evento faz nem o que é o destino: tipo e destino são
 * códigos do módulo. O destino é por evento, e não por quadro — na mesma fase,
 * um evento vai para a conta A e outro para a conta B.
 */
export interface StageEvent {
  /** A identidade dentro da fase. Quem gera é o editor. */
  id: string;
  type: string;
  targetId: string;
}

/** Um tipo do catálogo de eventos que o módulo oferece. */
export interface StageEventType {
  type: string;
  label: string;
  description?: string;
}

/** Para onde um evento pode ir — uma conta, no caso do Conta Azul. */
export interface StageEventTarget {
  id: string;
  label: string;
}

/** O que o editor de eventos precisa saber do módulo. */
export interface StageEventsConfig {
  catalog: readonly StageEventType[];
  /** Para onde cada evento pode ir. Escolhido por evento. */
  targets: readonly StageEventTarget[];
  /** Como o destino é chamado no formulário — "Conta do Conta Azul". */
  targetLabel: string;
  /** Um aviso no alto da seção — "nenhum evento executa ainda". */
  notice?: ReactNode;
}

/**
 * A lista com o evento novo no fim — ou `null` quando a fase já tem o mesmo
 * tipo para o mesmo destino. Duas linhas iguais disparariam o lançamento duas
 * vezes no mesmo movimento, e nenhuma das duas estaria errada sozinha.
 */
export function addStageEvent(
  events: readonly StageEvent[],
  event: StageEvent,
): StageEvent[] | null {
  const repeated = events.some(
    (e) => e.type === event.type && e.targetId === event.targetId,
  );
  return repeated ? null : [...events, event];
}

export function removeStageEvent(
  events: readonly StageEvent[],
  eventId: string,
): StageEvent[] {
  return events.filter((e) => e.id !== eventId);
}

/** Mesmos eventos, na mesma ordem — o "nada mudou" do formulário de fase. */
export function sameStageEvents(
  a: readonly StageEvent[],
  b: readonly StageEvent[],
): boolean {
  return (
    a.length === b.length &&
    a.every((e, i) => e.type === b[i]!.type && e.targetId === b[i]!.targetId)
  );
}
